import { InformationCircleIcon } from "@heroicons/react/20/solid";
import _ from "lodash";
import { useMemo } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@/atoms-react/button/Button.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import InputLabel from "@/atoms-react/input/Input.react";
import InputDate from "@/atoms-react/input/InputDate.react";
import InputPhone from "@/atoms-react/input/InputPhone.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import { Base, Label, SectionTitle } from "@/atoms-react/text/Text.react";
import { MiniAnimatedShield } from "@/components/animations/minishield/index.react";
import { Gender } from "@/features/auth/types";
import { LegalEntityLinkDto, MandateService, OlkyPassProLinkType } from "@/features/company/types";
import { setEmptyOrStr } from "@/features/company/utils";
import { Document } from "@/features/user/document/types";
import {
  FullCountry,
  StaticActivity,
  StaticCSP,
  StaticMandateService,
  StaticNatureMandate
} from "@/features/user/types";
import {
  FAVOURITE_COUNTRY_CODES,
  FRENCH_CODE_SHORT,
  PHONE_COUNTRY_CODES
} from "@/features/utils/constants";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { UploadDocumentInputRepresentative } from "@/molecules/upload-document-input-Representative";
import { isEmailValid } from "@/utils/regex-utils";
import {
  StaticLinkNatureRelationEnum,
  staticLinkNatureRelation
} from "@/utils/static-data/nature-relation";
import { RepresentativeConfiguration } from "../../AddCompanyModal/RepresentativeConfiguration";

type AddCompanyFormPrps = {
  onValidate: (data: LegalEntityLinkDto) => void;
  countries: FullCountry[];
  loading: boolean;
  agent: boolean;
  isPendingNatureMandates: boolean;
  natureMandates: StaticNatureMandate[] | undefined;
  mandateServices: StaticMandateService[] | undefined;

  CSPCodes:
    | {
        cspCodes: StaticCSP[];
      }
    | undefined;
  activitiesCsp:
    | {
        activities: StaticActivity[];
      }
    | undefined;
  editableRepresentative?: LegalEntityLinkDto;
  infoAgentEditable?: boolean;
  documentUploaded?: Document;
  onDownload: (data: {
    UUID: string | null;
    algoregDocumentId: string;
    isAgentLe?: boolean;
  }) => Promise<Blob>;
};
export const AddRepresentative = ({
  countries,
  onValidate,
  CSPCodes,
  activitiesCsp,
  natureMandates,
  isPendingNatureMandates,
  agent,
  editableRepresentative,
  loading,
  infoAgentEditable = false,
  documentUploaded,
  onDownload
}: AddCompanyFormPrps) => {
  const { t } = useTranslation(["pass", "atoms"]);
  const { getLanguage } = usei18n();
  const labelInputDivClassName = "md:flex md:space-x-6";
  const labelClassName = "flex w-40 min-w-40 mb-2 md:mb-0";
  const inputClassName = "flex grow ";
  const cspWithoutActivity = { CHÔMEUR: "14", INACTIF: "18", MILITAIRE: "13", ETUDIANT: "15" };
  const methods = useForm<LegalEntityLinkDto>({
    defaultValues: editableRepresentative
      ? {
          ...editableRepresentative,
          naturalPerson: {
            ...editableRepresentative?.naturalPerson,
            birthDate: editableRepresentative?.naturalPerson?.birthDate
              ? new Date(editableRepresentative?.naturalPerson?.birthDate)
              : undefined,
            taxAddress: editableRepresentative?.naturalPerson?.taxAddress?.countryUuid
              ? editableRepresentative?.naturalPerson?.taxAddress
              : undefined
          }
        }
      : {
          naturalPerson: null,
          mandatoryInformation: agent
            ? {
                linkType: OlkyPassProLinkType.AGENT,

                isCommunicationContact: true,
                mandateServices: [MandateService.ALIASES_MANAGEMENT],

                isAgentLe: undefined
              }
            : {
                boRelations: [],
                boRelationOther: ""
              }
        }
  });

  const countryFromMobileCode = useMemo(
    () =>
      _.invert(PHONE_COUNTRY_CODES)[editableRepresentative?.naturalPerson?.mobileCode ?? ""] ??
      undefined,
    [editableRepresentative]
  );
  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    setValue,
    watch
  } = methods;

  const onSubmit: SubmitHandler<LegalEntityLinkDto> = (data) => {
    onValidate(data);
  };
  const cspSelection = watch("naturalPerson.cspUuid");
  const handleCheckboxChange = (
    natureChecked: string,
    isChecked: boolean,
    boRelations: StaticLinkNatureRelationEnum[]
  ) => {
    const newElement = isChecked
      ? boRelations
        ? [...boRelations, natureChecked]
        : [natureChecked]
      : boRelations.filter((nature) => nature !== natureChecked);
    return newElement;
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mt-5 flex flex-col justify-between border-2 border-slate-50 p-4 dark:border-slate-700">
          <div className="mb-10 flex w-full flex-row justify-center p-4">
            {agent ? (
              infoAgentEditable ? (
                <SectionTitle> {t("business_links.add_link.title.edit_agent")} </SectionTitle>
              ) : (
                <SectionTitle> {t("business_links.add_link.title.agent")} </SectionTitle>
              )
            ) : infoAgentEditable ? (
              <SectionTitle>{t("business_links.add_link.title.edit_representative")}</SectionTitle>
            ) : (
              <SectionTitle>{t("business_links.add_link.title.representative")}</SectionTitle>
            )}
          </div>
          <div className="flex flex-row">
            <div
              className={twMerge(
                `flex w-full flex-start justify-center rounded-sm bg-bottom bg-cover md:w-1/5 lg:px-5`,
                "h-[100px] bg-[url('/images/olkypass/declarative_kyc_bg.png')]"
              )}
            >
              <div className={"flex flex-col justify-center"}>
                {
                  <MiniAnimatedShield
                    style="bottom-14 left-4 w-5 h-3"
                    colors="#D11E1F"
                    percentage={45}
                  />
                }
              </div>
            </div>
            <div className="mt-2 flex w-full w-full flex-row content-center justify-center md:mt-0 md:ml-4 md:flex-col">
              <div className="flex flex-row whitespace-nowrap">
                <Base className="font-semibold text-base text-slate-500">
                  {t("olkypass.mainPage.olkypass.levelPage.status_declarative")}
                </Base>
                <InformationCircleIcon
                  className="ml-2 h-5 w-5 rounded-full bg-primary text-white dark:text-slate-900"
                  data-tooltip={t(
                    "olkypass.mainPage.olkypass.levelPage.declarative_sub_description"
                  )}
                  data-position="bottom"
                />
              </div>
            </div>
          </div>
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>
                * {t("business_links.add_link.input.gender")}
              </Label>
            </div>
            <div className={inputClassName}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <Dropdown
                      placeholder={""}
                      dataCy="agent_gender"
                      required={!!errors?.naturalPerson?.gender}
                      touched={!!errors?.naturalPerson?.gender}
                      data-tooltip={"*" + t("business_links.add_link.input.gender")}
                      items={[
                        { id: Gender.Man, title: t("flow.id_control.input.gender_man") },
                        { id: Gender.Woman, title: t("flow.id_control.input.gender_woman") }
                      ].map((g) => ({
                        key: g.id.toString(),
                        value: g.title,

                        render: ({ key }) => (
                          <div className={"flex w-full"} key={key}>
                            <Base className={"ml-2"}>{g.title}</Base>
                          </div>
                        )
                      }))}
                      value={value}
                      autoComplete="country"
                      className={twMerge(
                        "w-full",
                        errors?.naturalPerson?.gender && "border-red-500"
                      )}
                      onChange={(value) => {
                        onChange(value.key);
                      }}
                    />
                  </div>
                )}
                name="naturalPerson.gender"
              />
            </div>
          </div>
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>
                {t("business_links.add_link.input.name_lastname")}
              </Label>
            </div>
            <div className={inputClassName + "flex flex-row justify-between gap-x-2"}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                name="naturalPerson.lastName"
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <InputLabel
                      dataCy="last_name_input"
                      data-tooltip={t("business_links.add_link.input.last_name")}
                      placeholder={t("business_links.add_link.input.last_name")}
                      value={value ?? undefined}
                      error={errors.naturalPerson?.lastName ? t("atoms:general.required") : ""}
                      required={false}
                      onChange={onChange}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name="naturalPerson.firstName"
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <InputLabel
                      dataCy="first_name_input"
                      data-tooltip={t("business_links.add_link.input.name")}
                      value={value ?? undefined}
                      required={false}
                      error={errors.naturalPerson?.firstName ? t("atoms:general.required") : ""}
                      placeholder={t("business_links.add_link.input.name")}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>
                {t("business_links.add_link.input.common_name")}
              </Label>
            </div>
            <div className={inputClassName + "flex flex-row justify-between gap-x-2"}>
              <Controller
                control={control}
                name="naturalPerson.commonName"
                defaultValue={null}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    data-tooltip={t("business_links.add_link.input.common_name")}
                    placeholder={t("business_links.add_link.input.common_name")}
                    required={false}
                    value={value ?? undefined}
                    onChange={(value) => onChange(setEmptyOrStr(value))}
                  />
                )}
              />
            </div>
          </div>
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>
                {t("business_links.add_link.input.nationality")}
              </Label>
            </div>
            <div className={inputClassName}>
              <Controller
                control={control}
                name="naturalPerson.countryOfCitizenshipUuid"
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <Dropdown
                      placeholder={t("business_links.add_link.input.nationality")}
                      data-tooltip={t("business_links.add_link.input.nationality")}
                      dataCy="agent_citizenship"
                      required={!!errors.naturalPerson?.countryOfCitizenshipUuid}
                      touched={!!errors.naturalPerson?.countryOfCitizenshipUuid}
                      search={true}
                      items={
                        (countries ?? []).map((country) => ({
                          key: country.uuid?.toString() ?? "",
                          value: country.defaultName,
                          icon: <Flag countryCode={country.alpha2} />,
                          render: ({ key }) => (
                            <div className={"flex w-full"} key={key}>
                              <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                              <Base className={"ml-2"}>{country.defaultName}</Base>
                            </div>
                          ),
                          groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                            ? "default"
                            : undefined
                        })) ?? []
                      }
                      value={value}
                      autoComplete="country"
                      className={twMerge(
                        "w-full",
                        errors.naturalPerson?.countryOfCitizenshipUuid && "border-red-500"
                      )}
                      onChange={(value) => {
                        onChange(value.key);
                      }}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>
                {t("business_links.add_link.input.birthdate")}
              </Label>
            </div>
            <div className={inputClassName}>
              <Controller
                control={control}
                name="naturalPerson.birthDate"
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => {
                  return (
                    <div className="flex w-full flex-col">
                      <InputDate
                        dataCy="birthdate_input"
                        autoComplete="bday"
                        required={false}
                        value={value ?? null}
                        data-tooltip={t("business_links.add_link.input.birthdate")}
                        placeholder={t("flow.id_control.input.birth_date_placeholder")}
                        className={twMerge(
                          "w-full",
                          errors.naturalPerson?.birthDate && "border-red-500"
                        )}
                        onChange={(value) => {
                          if (!value) return;
                          onChange(value);
                        }}
                      />
                    </div>
                  );
                }}
              />
            </div>
          </div>
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>
                * {t("business_links.add_link.input.stateofBirth")}
              </Label>
            </div>
            <div className={inputClassName + "flex flex-row justify-between gap-x-2"}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                name="naturalPerson.birthState"
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <InputLabel
                      dataCy="birth_city_input"
                      data-tooltip={t("flow.id_control.input.business.city_label")}
                      placeholder={t("flow.id_control.input.business.city_placeholder")}
                      required={false}
                      error={errors.naturalPerson?.birthState && t("atoms:general.required")}
                      value={value as string}
                      onChange={(value) => {
                        onChange(value);
                      }}
                    />
                  </div>
                )}
              />
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                name="naturalPerson.birthCountryUuid"
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <Dropdown
                      search={true}
                      placeholder={t("flow.id_control.input.country_of_birth_placeholder")}
                      required={!!errors.naturalPerson?.birthCountryUuid}
                      touched={!!errors.naturalPerson?.birthCountryUuid}
                      dataCy="birth_country_country_dropdown"
                      items={
                        (countries ?? []).map((country) => ({
                          key: country.uuid?.toString() ?? "",
                          value: country.defaultName,
                          icon: <Flag countryCode={country.alpha2} />,
                          render: ({ key }) => (
                            <div className={"flex w-full"} key={key}>
                              <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                              <Base className={"ml-2"}>{country.defaultName}</Base>
                            </div>
                          ),
                          groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                            ? "default"
                            : undefined
                        })) ?? []
                      }
                      value={value}
                      autoComplete="country"
                      className={twMerge(
                        "w-full",
                        errors.naturalPerson?.birthCountryUuid && "border-red-500"
                      )}
                      onChange={(value) => {
                        onChange(value.key);
                      }}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          <hr className="mt-6 border-slate-50 dark:border-slate-700"></hr>
          <div className='div className="flex mb-5 justify-between'>
            <Base className="mt-6 hidden text-slate-300 md:block">
              {t("business_links.add_link.title.address")}
            </Base>
          </div>
          <div className={twMerge(labelInputDivClassName)}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>
                {t("olkypass.companyPage.basicInfo.input.road")}
              </Label>
            </div>
            <div className={inputClassName}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                name="naturalPerson.homeAddress.address1"
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <InputLabel
                      dataCy="address_input"
                      data-tooltip={t("olkypass.companyPage.basicInfo.input.road")}
                      placeholder={t("flow.id_control.input.address_placeholder")}
                      required={false}
                      error={
                        errors.naturalPerson?.homeAddress?.address1 && t("atoms:general.required")
                      }
                      value={value ?? undefined}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>
                {t("olkypass.companyPage.basicInfo.input.city_zip")}
              </Label>
            </div>
            <div className={inputClassName + "flex flex-row justify-between gap-x-2"}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                name="naturalPerson.homeAddress.city"
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <InputLabel
                      dataCy="address_city_input"
                      data-tooltip={t("flow.id_control.input.business.city_label")}
                      placeholder={t("flow.id_control.input.business.city_placeholder")}
                      value={value ?? undefined}
                      required={false}
                      error={errors.naturalPerson?.homeAddress?.city && t("atoms:general.required")}
                      onChange={onChange}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                rules={{
                  required: true
                }}
                name="naturalPerson.homeAddress.zipCode"
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <InputLabel
                      dataCy="address_zip_code_input"
                      value={value ?? undefined}
                      required={false}
                      data-tooltip={t("flow.id_control.input.zip_label")}
                      placeholder={t("flow.id_control.input.zip_placeholder")}
                      error={
                        errors.naturalPerson?.homeAddress?.zipCode && t("atoms:general.required")
                      }
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>
                {t("olkypass.companyPage.basicInfo.input.country")}
              </Label>
            </div>
            <div className={inputClassName}>
              <Controller
                rules={{
                  required: true
                }}
                control={control}
                name="naturalPerson.homeAddress.countryUuid"
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <Dropdown
                      placeholder={t("flow.id_control.input.country_of_residence_placeholder")}
                      data-tooltip={t("olkypass.companyPage.basicInfo.input.country")}
                      required={!!errors?.naturalPerson?.homeAddress?.countryUuid}
                      touched={!!errors?.naturalPerson?.homeAddress?.countryUuid}
                      dataCy="agent_homeAddress"
                      search={true}
                      items={
                        (countries ?? []).map((country) => ({
                          key: country.uuid?.toString() ?? "",
                          value: country.defaultName,
                          icon: <Flag countryCode={country.alpha2} />,
                          render: ({ key }) => (
                            <div className={"flex w-full"} key={key}>
                              <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                              <Base className={"ml-2"}>{country.defaultName}</Base>
                            </div>
                          ),
                          groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                            ? "default"
                            : undefined
                        })) ?? []
                      }
                      value={value}
                      autoComplete="country"
                      className={twMerge(
                        "w-full",
                        errors?.naturalPerson?.homeAddress?.countryUuid && "border-red-500"
                      )}
                      onChange={(value) => {
                        onChange(value.key);
                      }}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          <hr className="mt-6 border-slate-50 dark:border-slate-700"></hr>
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <Label className={labelClassName}>{t("flow.id_control.input.country_nif_label")}</Label>
            <div className={inputClassName}>
              <Controller
                control={control}
                name="naturalPerson.taxAddress.countryUuid"
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    placeholder={t("flow.id_control.input.country_nif_label")}
                    data-tooltip={t("flow.id_control.input.country_nif_label")}
                    search={true}
                    items={
                      (countries ?? []).map((country) => ({
                        key: country.uuid?.toString() ?? "",
                        value: country.defaultName,
                        icon: <Flag countryCode={country.alpha2} />,
                        render: ({ key }) => (
                          <div className={"flex w-full"} key={key}>
                            <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                            <Base className={"ml-2"}>{country.defaultName}</Base>
                          </div>
                        ),
                        groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                          ? "default"
                          : undefined
                      })) ?? []
                    }
                    value={value}
                    autoComplete="country"
                    className={twMerge(
                      "w-full",
                      errors?.naturalPerson?.taxAddress?.countryUuid && "border-red-500"
                    )}
                    onChange={(value) => {
                      onChange(value.key);
                      if (!getValues("naturalPerson.taxAddress.isDirty"))
                        setValue("naturalPerson.taxAddress.isDirty", true);
                    }}
                  />
                )}
              />
            </div>
          </div>
          <div className={twMerge(labelInputDivClassName, "mt-4")}>
            <Label className={labelClassName}>
              {t("olkypass.companyPage.basicInfo.input.nif")}
            </Label>
            <div className={inputClassName}>
              <Controller
                control={control}
                disabled={watch("naturalPerson.taxAddress.countryUuid") === null}
                name="naturalPerson.taxAddress.tin"
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    data-tooltip={t("olkypass.companyPage.basicInfo.input.nif")}
                    placeholder={t("olkypass.companyPage.basicInfo.input.nif")}
                    required={false}
                    value={value ?? undefined}
                    onChange={(value) => {
                      onChange(setEmptyOrStr(value));
                      if (!getValues("naturalPerson.taxAddress.isDirty"))
                        setValue("naturalPerson.taxAddress.isDirty", true);
                    }}
                  />
                )}
              />
            </div>
          </div>
          <div className={twMerge(labelInputDivClassName, "mt-4")}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>{t("business_links.add_link.input.csp")} </Label>
            </div>
            <div className={inputClassName}>
              <Controller
                control={control}
                name="naturalPerson.cspUuid"
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <Dropdown
                      value={value ?? null}
                      placeholder={t("flow.id_control.input.csp_placeholder")}
                      data-tooltip={t("business_links.add_link.input.csp")}
                      required={false}
                      items={(CSPCodes?.cspCodes ?? []).map((code) => ({
                        key: code.uuid.toString(),
                        value: code.defaultName
                      }))}
                      search={true}
                      className={"w-full"}
                      onChange={({ key }) => onChange(key)}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          {cspSelection && !Object.values(cspWithoutActivity).includes(cspSelection) && (
            <div className={twMerge(labelInputDivClassName, "mt-6")}>
              <div className="flex flex-col justify-center">
                <Label className={labelClassName}>
                  {t("business_links.add_link.input.activity")}
                </Label>
              </div>
              <div className={inputClassName}>
                <div className="flex w-full flex-col">
                  <Controller
                    control={control}
                    defaultValue={null}
                    rules={{
                      required: Object.values(cspWithoutActivity).includes(cspSelection)
                    }}
                    name="naturalPerson.activityUuid"
                    render={({ field: { onChange, value } }) => (
                      <Dropdown
                        value={value}
                        search={true}
                        placeholder={t("flow.id_control.input.activity_placeholder")}
                        required={false}
                        data-tooltip={t("business_links.add_link.input.activity")}
                        className={twMerge(
                          "w-full",
                          errors?.naturalPerson?.activityUuid && "border-red-500"
                        )}
                        items={(activitiesCsp?.activities ?? []).map((activity) => ({
                          key: activity.uuid.toString(),
                          value: activity.defaultName
                        }))}
                        onChange={({ key }) => onChange(key)}
                      />
                    )}
                  />
                  {errors.naturalPerson?.activityUuid && (
                    <Base className="text-red-500 text-sm"> {t("atoms:general.required")}.</Base>
                  )}
                </div>
              </div>
            </div>
          )}
          <hr className="mt-6 border-slate-50 dark:border-slate-700"></hr>
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>{t("business_links.add_link.input.email")}</Label>
            </div>
            <div className={inputClassName}>
              <Controller
                control={control}
                defaultValue={null}
                rules={{
                  validate: (x) => (x ? isEmailValid(x) : true)
                }}
                name="naturalPerson.mailAddress"
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <InputLabel
                      dataCy="company-create-mandatary-email"
                      data-tooltip={t("business_links.add_link.input.email")}
                      placeholder={t("business_links.add_link.input.email")}
                      required={false}
                      noLatinCheck={true}
                      className="w-full"
                      error={
                        errors.naturalPerson?.mailAddress ? t("business_links.error.email") : ""
                      }
                      value={value ?? undefined}
                      onChange={(value) => {
                        onChange(setEmptyOrStr(value));
                      }}
                    />
                    {errors.naturalPerson?.mailAddress && (
                      <Base className="text-red-500 text-sm"> {t("atoms:general.required")}.</Base>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
          <div className={twMerge(labelInputDivClassName, "mt-6")}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>{t("business_links.add_link.input.Mobile")}</Label>
            </div>
            <div className={twMerge(inputClassName, "w-full")}>
              <InputPhone
                value={{
                  countryCode: "",
                  phone: watch("naturalPerson.phoneNumber") ?? undefined,
                  international: undefined,
                  countryPrefix: watch("naturalPerson.mobileCode") ?? "",
                  valid: false
                }}
                className="w-full"
                autoComplete="tel"
                onChange={(value) => {
                  setValue("naturalPerson.phoneNumber", value.phone);
                  setValue("naturalPerson.mobileCode", value.countryPrefix);
                }}
                initialCountry={countryFromMobileCode ?? FRENCH_CODE_SHORT}
              />
            </div>
          </div>

          <hr className="mt-6 mb-6 border-slate-50 dark:border-slate-700"></hr>
          <div className={labelInputDivClassName}>
            <div className="flex flex-col justify-center">
              <Label className={labelClassName}>
                {t("business_links.add_link.input.document")}
              </Label>
            </div>
            <div className={inputClassName}>
              <Controller
                control={control}
                defaultValue={null}
                name="naturalPerson.identityJustification"
                render={({ field: { onChange, value } }) => (
                  <UploadDocumentInputRepresentative
                    documentToDownload={documentUploaded}
                    linkUuid={editableRepresentative?.linkUuid}
                    onDownload={onDownload}
                    onError={(messagekey: string) => {
                      toast.error(t(messagekey));
                    }}
                    document={value ?? null}
                    onChange={(value) => {
                      onChange(value);
                      setValue("naturalPerson.identityJustification.isDirty", true);
                    }}
                  />
                )}
              />
            </div>
          </div>
          <hr className="mt-6 border-slate-50 dark:border-slate-700"></hr>

          {!agent ? (
            <>
              <hr className="mt-6 border-slate-50 dark:border-slate-700"></hr>
              <div className='div className="flex mb-5 justify-between'>
                <Base className="mt-6 hidden text-slate-300 md:block">
                  {t("business_links.add_link.input.relation_type.title")}
                </Base>
              </div>
              <div className={twMerge(labelInputDivClassName, "mt-2")}>
                <Label className={labelClassName}>
                  {t("business_links.add_link.input.pourcentage")}
                </Label>
                <div className={inputClassName}>
                  <Controller
                    control={control}
                    rules={{
                      required: true
                    }}
                    name="mandatoryInformation.boPercentage"
                    render={({ field: { onChange, value } }) => (
                      <div className="flex w-full flex-col">
                        <InputLabel
                          dataCy="input-bo-percentage"
                          type="number"
                          min={1}
                          max={100}
                          required={false}
                          value={value?.toString() ?? undefined}
                          suffixIcon={<Base className="font-bold">%</Base>}
                          error={
                            errors.mandatoryInformation?.boPercentage
                              ? t("atoms:general.required")
                              : ""
                          }
                          onChange={(value) => {
                            let response: string | null = null;
                            if (value != "") {
                              if (parseInt(value) < 0) {
                                response = `0`;
                              } else if (parseInt(value) >= 100) {
                                response = `100`;
                              } else response = value;
                            }
                            onChange(response);
                          }}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className={twMerge(labelInputDivClassName, "mt-2")}>
                <Label className={twMerge("my-4 mt-8", labelClassName)}>
                  <div className="flex flex-col">
                    {t("business_links.add_link.input.relation_type.title")}
                    <Label className="font-normal">
                      {t("business_links.add_link.input.relation_type.title_choice")}
                    </Label>
                  </div>
                  {/* TODO : tooltip text needs to be validate
                    <ExclamationCircleIcon
                      className="ml-2 h-7 w-7 text-pblue-500"
                      data-tooltip={t("business_links.add_link.input.relation_type.tooltip")}
                    /> 
                  */}
                </Label>

                <Controller
                  control={control}
                  rules={{
                    required: true
                  }}
                  name="mandatoryInformation.boRelations"
                  render={({ field: { onChange, value } }) => (
                    <div
                      className={
                        "my-4 mt-8 mb-2 grid w-full min-w-40 grid-cols-1 gap-2 md:mb-0 md:grid-cols-2"
                      }
                    >
                      {Object.entries(staticLinkNatureRelation).map(([key, valueItem]) => (
                        <Checkbox
                          dataCy={key}
                          key={key}
                          value={value?.some((nature: string) => nature === key)}
                          className={errors.mandatoryInformation?.boRelations && "border-red-500"}
                          onChange={(newValue) => {
                            const arr = handleCheckboxChange(key, newValue, value ?? []);
                            onChange(arr);
                          }}
                        >
                          <Base className="mb-1 text-black">
                            {getLanguage() === "fr" ? valueItem.content_fr : valueItem.content_en}
                          </Base>
                        </Checkbox>
                      ))}
                    </div>
                  )}
                />
              </div>
              <div className={twMerge(labelInputDivClassName, "mt-2")}>
                <Controller
                  control={control}
                  rules={{
                    required: watch("mandatoryInformation.boRelations")?.includes(
                      StaticLinkNatureRelationEnum.OTHER
                    )
                  }}
                  name="mandatoryInformation.boRelationOther"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <>
                        {watch("mandatoryInformation.boRelations")?.includes(
                          StaticLinkNatureRelationEnum.OTHER
                        ) && (
                          <>
                            <Label className={labelClassName}></Label>
                            <InputLabel
                              dataCy="company-create-mandatary-nature-mandate-other-input"
                              placeholder={t(
                                "business_links.add_link.input.relationTypeOther.placeholder"
                              )}
                              required={watch("mandatoryInformation.boRelations")?.includes(
                                StaticLinkNatureRelationEnum.OTHER
                              )}
                              noLatinCheck={true}
                              className="a w-full"
                              error={
                                errors.mandatoryInformation?.boRelations
                                  ? t("business_links.add_link.input.relationTypeOther.error")
                                  : ""
                              }
                              value={value ?? ""}
                              onChange={onChange}
                            />
                          </>
                        )}
                      </>
                    );
                  }}
                />
              </div>
            </>
          ) : (
            <RepresentativeConfiguration
              natureMandates={natureMandates}
              pendingNatureMandates={isPendingNatureMandates}
            />
          )}
          <div className="mt-8 flex w-full flex-row justify-center">
            <Button
              type="submit"
              loading={loading}
              className="w-1/5 rounded-lg bg-primary"
              dataCy="submit-button"
            >
              {infoAgentEditable ? t("business_links.edit") : t("business_links.button_3")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
